import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { clientAPI, documentsAPI, platformAPI, boardsAPI, authAPI } from '../api';
import OwnerInlineText from '../components/OwnerInlineText';
import NoteAttachmentCard from '../components/NoteAttachmentCard';
import ClientViewEditorSidebar from './BoardEditor/components/ClientViewEditorSidebar';
import ImageLightbox from '../components/ImageLightbox';
import PlatformFooter from '../components/PlatformFooter';
import BatchUploadStatus from '../components/BatchUploadStatus';
import { useBatchUpload } from '../hooks/useBatchUpload';
import { getTranslation } from '../i18n';
import { resolveText, normalizeByLang } from '../utils/clientTexts';
import { getReactionButtons, getReactionLabel } from '../utils/reactionLabels';
import BoardCoverOverlay from '../components/BoardCoverOverlay';

const TONE_MAP = {
  'default':    { page: '#faf8f5', section: '#ffffff', border: '#ece9e4', text: '#4a4540', textLight: '#8a847b', accent: '#b8a992' },
  'ivory':      { page: '#faf8f5', section: '#ffffff', border: '#ece9e4', text: '#4a4540', textLight: '#8a847b', accent: '#b8a992' },
  'warm-sand':  { page: '#f5f0e8', section: '#faf7f2', border: '#e8e0d4', text: '#4a4035', textLight: '#8a7e72', accent: '#b09878' },
  'soft-grey':  { page: '#f0f0ee', section: '#f7f7f6', border: '#e2e2df', text: '#3c3c3a', textLight: '#7a7a76', accent: '#9a9a96' },
  'dusty-blue': { page: '#edf1f5', section: '#f5f7fa', border: '#dbe1e8', text: '#3a4048', textLight: '#6e7880', accent: '#8ea0b0' },
};

function deriveCustomToneCV(hex) {
  if (!hex || hex.length !== 7) return TONE_MAP['default'];
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const mix = (c, t, w) => Math.round(c * w + t * (1 - w));
  const toHex = (r2, g2, b2) => '#' + [r2, g2, b2].map(c => c.toString(16).padStart(2, '0')).join('');
  return {
    page:      toHex(mix(r, 255, 0.08), mix(g, 255, 0.08), mix(b, 255, 0.08)),
    section:   toHex(mix(r, 255, 0.03), mix(g, 255, 0.03), mix(b, 255, 0.03)),
    border:    toHex(mix(r, 230, 0.12), mix(g, 230, 0.12), mix(b, 230, 0.12)),
    text:      toHex(mix(r, 50, 0.2), mix(g, 50, 0.2), mix(b, 50, 0.2)),
    textLight: toHex(mix(r, 120, 0.2), mix(g, 120, 0.2), mix(b, 120, 0.2)),
    accent:    toHex(mix(r, 160, 0.35), mix(g, 160, 0.35), mix(b, 160, 0.35)),
  };
}

function resolveTone(toneId, customColor) {
  if (toneId === 'custom' && customColor) return deriveCustomToneCV(customColor);
  return TONE_MAP[toneId] || TONE_MAP['default'];
}

// Inspiration Card Component with note functionality
const InspirationCard = ({ inspiration, isLocked, onDelete, onNoteChange, onImageClick }) => {
  const [note, setNote] = useState(inspiration.note || '');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = React.useRef(null);

  // Focus input when entering edit mode
  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleSave = async () => {
    const trimmed = note.trim();
    if (trimmed !== (inspiration.note || '')) {
      setSaving(true);
      await onNoteChange(trimmed || null);
      setSaving(false);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      setNote(inspiration.note || '');
      setIsEditing(false);
    }
  };

  const handleRemoveNote = async () => {
    setNote('');
    setIsEditing(false);
    await onNoteChange(null);
  };

  const hasNote = note.trim().length > 0;

  return (
    <div className="inspiration-card" style={{ touchAction: 'auto', position: 'relative' }}>
      <div className="image-wrapper" style={{ cursor: 'zoom-in' }} onClick={onImageClick}>
        <img src={inspiration.image_data} alt="" style={{ touchAction: 'auto' }} />
        {!isLocked && (
          <button 
            className="delete-btn"
            onClick={onDelete}
            data-testid={`delete-inspiration-${inspiration.id}`}
          >
            ✕
          </button>
        )}
      </div>
      
      {/* Minimal Note Section */}
      <div style={{ 
        padding: hasNote || isEditing ? '10px 12px' : '8px 12px',
        minHeight: hasNote || isEditing ? 'auto' : '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {/* State 1: No note, not editing - show subtle add link */}
        {!hasNote && !isEditing && !isLocked && (
          <button 
            onClick={() => setIsEditing(true)}
            style={{ 
              background: 'none', 
              border: 'none', 
              color: 'var(--kahla-taupe)',
              fontSize: '11px',
              cursor: 'pointer',
              opacity: 0.6,
              transition: 'opacity 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
            onMouseEnter={(e) => e.target.style.opacity = 1}
            onMouseLeave={(e) => e.target.style.opacity = 0.6}
            data-testid={`add-note-btn-${inspiration.id}`}
          >
            <span style={{ fontSize: '13px' }}>+</span> add note
          </button>
        )}

        {/* State 2: Editing mode - inline input overlay */}
        {isEditing && !isLocked && (
          <div style={{ width: '100%', position: 'relative' }}>
            <input
              ref={inputRef}
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 200))}
              onBlur={handleSave}
              onKeyDown={handleKeyDown}
              placeholder="What do you love about this?"
              maxLength={200}
              disabled={saving}
              style={{ 
                width: '100%',
                padding: '8px 10px',
                fontSize: '12px',
                fontFamily: 'inherit',
                fontStyle: 'italic',
                color: 'var(--kahla-text)',
                border: 'none',
                borderBottom: '1px solid var(--kahla-taupe)',
                background: 'transparent',
                outline: 'none',
                textAlign: 'center',
              }}
              data-testid={`note-input-${inspiration.id}`}
            />
            {saving && (
              <span style={{ 
                position: 'absolute', 
                right: '4px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                fontSize: '10px', 
                color: 'var(--kahla-taupe)',
                opacity: 0.7,
              }}>
                saving...
              </span>
            )}
          </div>
        )}

        {/* State 3: Has note, not editing, not locked - show as clean caption with edit capability */}
        {hasNote && !isEditing && !isLocked && (
          <div
            onClick={() => !isLocked && setIsEditing(true)}
            style={{
              width: '100%',
              cursor: isLocked ? 'default' : 'pointer',
              position: 'relative',
              padding: '10px 14px',
              background: 'rgba(250, 247, 242, 0.85)',
              border: '1px solid rgba(74, 69, 64, 0.08)',
              borderRadius: 2,
            }}
            data-testid={`note-display-${inspiration.id}`}
          >
            <p style={{
              fontFamily: 'Cormorant Garamond, Playfair Display, serif',
              fontSize: 14,
              color: 'var(--kahla-charcoal)',
              margin: 0,
              lineHeight: 1.55,
              textAlign: 'left',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}>
              {note}
            </p>
            {!isLocked && (
              <button
                onClick={(e) => { e.stopPropagation(); handleRemoveNote(); }}
                style={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  background: 'none',
                  border: 'none',
                  fontSize: '11px',
                  color: 'var(--kahla-taupe)',
                  cursor: 'pointer',
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  padding: '2px 4px',
                }}
                className="note-remove-btn"
                data-testid={`remove-note-btn-${inspiration.id}`}
              >
                ✕
              </button>
            )}
          </div>
        )}

        {/* State 4: Locked with note - just show text */}
        {hasNote && isLocked && (
          <div
            style={{
              width: '100%',
              padding: '10px 14px',
              background: 'rgba(250, 247, 242, 0.85)',
              border: '1px solid rgba(74, 69, 64, 0.08)',
              borderRadius: 2,
            }}
            data-testid={`note-display-locked-${inspiration.id}`}
          >
            <p style={{
              fontFamily: 'Cormorant Garamond, Playfair Display, serif',
              fontSize: 14,
              color: 'var(--kahla-charcoal)',
              margin: 0,
              lineHeight: 1.55,
              textAlign: 'left',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}>
              {note}
            </p>
          </div>
        )}

        {/* State 5: Locked without note - show nothing */}
      </div>
    </div>
  );
};

// ── Mobile sticky bottom navigation ────────────────────────────────────
// Renders only below 760px viewport. Surfaces 2–4 quick-jump tabs for
// the sections that actually exist on the current board (driven by the
// same `visible` rules as renderSection) so the client never has to
// scroll past the gallery to reach Notes / Documents / Inspirations.
//
// Visual language matches the rest of the client view:
// translucent off-white with a hairline top border, 1px-letterspaced
// Playfair labels, and a tiny editorial inline-SVG glyph per tab.
// Active tab uses the same charcoal as buttons elsewhere.
const MobileBottomNav = ({ tabs, activeKey, onSelect, visible = true }) => {
  if (!tabs?.length) return null;
  return (
    <nav
      data-testid="client-mobile-bottom-nav"
      aria-label="Sections"
      aria-hidden={!visible}
      style={{
        position: 'fixed',
        // Float gently above the viewport bottom — reads as quiet
        // typography over the page, not a fixed app chrome bar.
        bottom: 'calc(env(safe-area-inset-bottom) + 6px)',
        left: '50%',
        // Slide down + fade out when hidden so it doesn't sit over
        // content while the client reads. Reappears the moment they
        // scroll back up.
        transform: `translateX(-50%) translateY(${visible ? '0' : '24px'})`,
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'transform 0.32s ease, opacity 0.28s ease',
        zIndex: 950, // below modals (1000+), above scroll content
        display: 'inline-flex',
        alignItems: 'center',
        gap: '12px',
        padding: '6px 14px',
        // Almost invisible backdrop — just enough blur to ensure the
        // labels stay legible over any image, with no border / shadow
        // that would announce "this is a UI bar".
        background: 'rgba(255, 252, 247, 0.42)',
        backdropFilter: 'saturate(160%) blur(20px)',
        WebkitBackdropFilter: 'saturate(160%) blur(20px)',
        borderRadius: '2px',
      }}
    >
      {tabs.map((tab, idx) => {
        const active = tab.key === activeKey;
        return (
          <React.Fragment key={tab.key}>
            {idx > 0 && (
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-block',
                  width: '1px',
                  height: '8px',
                  background: 'var(--kahla-text-light)',
                  opacity: 0.35,
                }}
              />
            )}
            <button
              type="button"
              onClick={() => onSelect(tab.key)}
              data-testid={`client-nav-${tab.key}`}
              aria-pressed={active}
              style={{
                position: 'relative',
                padding: '2px 0 4px',
                background: 'transparent',
                border: 'none',
                color: active ? 'var(--kahla-charcoal)' : 'var(--kahla-text-light)',
                cursor: 'pointer',
                fontFamily: '"Playfair Display", serif',
                fontSize: '10px',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                fontWeight: 400,
                transition: 'color 0.25s ease',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {tab.label}
              {/* Hairline underline for the active section — the only
                  visual affordance. No background, no chip. */}
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: '1px',
                  background: 'var(--kahla-charcoal)',
                  opacity: active ? 1 : 0,
                  transition: 'opacity 0.25s ease',
                }}
              />
            </button>
          </React.Fragment>
        );
      })}
    </nav>
  );
};

// ── Icon helpers (inline SVG — no extra deps) ─────────────────────────
const navIcon = (path, size = 13) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {path}
  </svg>
);
const ICON_INSPIRATIONS = navIcon(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </>
);
const ICON_GALLERY = navIcon(
  <>
    <rect x="3" y="5" width="18" height="14" rx="1" />
    <circle cx="9" cy="11" r="1.5" />
    <path d="M21 16l-5-5-9 9" />
  </>
);
const ICON_FINAL = navIcon(
  <path d="M12 3.5l2.3 5.1 5.6.6-4.2 3.8 1.2 5.5L12 15.8l-4.9 2.7 1.2-5.5L4.1 9.2l5.6-.6z" />
);
const ICON_NOTES = navIcon(
  <>
    <path d="M5 5h14v12l-4 4H5z" />
    <path d="M9 9h6M9 13h4" />
  </>
);
const ICON_DOCUMENTS = navIcon(
  <>
    <path d="M7 3h7l5 5v13H7z" />
    <path d="M14 3v5h5" />
    <path d="M10 13h6M10 17h6" />
  </>
);

const ClientView = () => {
  const { shareToken } = useParams();
  document.title = 'Maestra';

  const [board, setBoard] = useState(null);
  // Board Cover Overlay — full-screen cinematic entrance. We keep the
  // dismiss decision in component state (not in board fields) so the
  // owner toggling things off mid-session re-evaluates cleanly. When
  // `first_visit_only` is true, we also store the dismissal in
  // localStorage keyed by share token so returning visits skip the
  // cover but other boards still surface theirs.
  const [coverDismissed, setCoverDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading] = useState(false);
  const [toast, setToast] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [inspirationLightboxIndex, setInspirationLightboxIndex] = useState(null);
  const [ptx, setPtx] = useState(null);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [showParticipantPicker, setShowParticipantPicker] = useState(false);

  // ── Owner Inline Edit Mode ─────────────────────────────────────────
  // Opened from BoardEditor as /board/{token}?edit=1. The owner edits
  // the REAL client page: every visible text is click-to-edit, the
  // sidebar holds only advanced options. Activated only after the
  // authenticated ownership check (boardsAPI.get) succeeds — clients
  // adding ?edit=1 by hand see the plain view.
  const navigate = useNavigate();
  const editRequested = typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('edit') === '1';
  const [editMode, setEditMode] = useState(false);
  const [ownerFA, setOwnerFA] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Mobile-only sticky bottom nav state. The nav itself is rendered
  // unconditionally for narrow viewports but only with tabs for the
  // sections that actually exist on this board (see `navTabs` below).
  const [pageNarrow, setPageNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 760 : false
  );
  const [activeSection, setActiveSection] = useState(null);
  // Hide-on-scroll-down, show-on-scroll-up so the nav doesn't sit
  // over content while clients are reading. Starts visible at the
  // top of the page; smoothly translates out when they scroll down,
  // reappears the moment they start scrolling back up.
  const [navVisible, setNavVisible] = useState(true);
  useEffect(() => {
    if (!pageNarrow) return;
    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - lastY;
        // Always show near the top — there's nothing to hide for yet.
        if (y < 80) {
          setNavVisible(true);
        } else if (delta > 6) {
          // Scrolling down — get out of the way.
          setNavVisible(false);
        } else if (delta < -6) {
          // Scrolling up — surface the nav for quick jumps.
          setNavVisible(true);
        }
        lastY = y;
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pageNarrow]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setPageNarrow(window.innerWidth < 760);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // Update the bottom-nav highlight as the user scrolls between sections.
  useEffect(() => {
    if (!pageNarrow) return;
    const ids = ['gallery', 'notes'];
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the most-visible entry; falls back to the previous active.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          const key = visible[0].target.dataset.section;
          if (key) setActiveSection(key);
        }
      },
      { rootMargin: '-25% 0px -55% 0px', threshold: [0, 0.25, 0.5] }
    );
    ids.forEach((k) => {
      const el = document.getElementById(`client-section-${k}`);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [pageNarrow, board]);
  const scrollToSection = useCallback((key) => {
    const el = document.getElementById(`client-section-${key}`);
    if (!el) return;
    // Bottom nav is ~64px tall + safe-area; offset the scroll so the
    // section's own heading lands well above it without being hidden
    // by the iOS URL bar / Android chrome.
    const top = el.getBoundingClientRect().top + window.scrollY - 12;
    window.scrollTo({ top, behavior: 'smooth' });
    setActiveSection(key);
  }, []);

  useEffect(() => {
    fetchBoard();
    platformAPI.getTexts().then(res => setPtx(res.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareToken]);

  useEffect(() => {
    if (!editRequested || !board?.id || editMode) return;
    let cancelled = false;
    (async () => {
      try {
        await boardsAPI.get(board.id);
        const { data } = await authAPI.getSettings();
        if (cancelled) return;
        setOwnerFA(data.feature_access || {});
        setEditMode(true);
      } catch (_e) { /* not the owner — plain client view */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editRequested, board?.id, editMode]);

  const fetchBoard = async () => {
    try {
      const { data } = await clientAPI.getBoard(shareToken);
      setBoard(data);
      // Board Cover Overlay — honor "show only on first visit" by
      // checking a per-board flag in localStorage. The check happens
      // here (after we know the board's cover settings) rather than at
      // render time so we don't briefly flash the cover for returning
      // visitors before dismissing it.
      if (data?.board_cover_enabled && data?.board_cover_first_visit_only) {
        try {
          const k = `maestra_cover_seen_${shareToken}`;
          if (localStorage.getItem(k) === '1') setCoverDismissed(true);
        } catch (e) { /* localStorage disabled — keep showing the cover */ }
      }
      // Shared decision: restore participant from localStorage.
      //
      // What's stored: just the numeric participant index (0, 1, 2…) chosen
      // by the client on this device — no token, no email, no PII. The board
      // itself is already authenticated by its share token in the URL, and
      // the server re-validates the participant index on every reaction. The
      // localStorage key is scoped by `shareToken` so a single browser used
      // across multiple boards keeps each board's pick isolated.
      //
      // Why localStorage (not sessionStorage): families/teams often reopen the
      // same client link across days. sessionStorage would force them to
      // re-pick their name every visit, which is a worse UX without adding
      // any real security — the data is non-sensitive UI state.
      if (data.shared_decision_mode) {
        const stored = localStorage.getItem(`maestra_participant_${shareToken}`);
        if (stored !== null) {
          setSelectedParticipant(parseInt(stored));
        } else {
          setShowParticipantPicker(true);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Board not found');
    } finally {
      setLoading(false);
    }
  };

  const selectParticipant = (pid) => {
    setSelectedParticipant(pid);
    setShowParticipantPicker(false);
    localStorage.setItem(`maestra_participant_${shareToken}`, String(pid));
  };

  const handleReaction = async (imageId, reaction) => {
    if (board.is_locked) return;
    
    try {
      const pid = board.shared_decision_mode ? selectedParticipant : undefined;
      await clientAPI.submitReaction(shareToken, imageId, reaction, null, pid);
      setBoard(prev => ({
        ...prev,
        images: prev.images.map(img => {
          if (img.id !== imageId) return img;
          if (board.shared_decision_mode && pid != null) {
            const reactions = { ...(img.reactions || {}) };
            reactions[String(pid)] = { reaction, comment: (reactions[String(pid)] || {}).comment || null };
            return { ...img, reactions };
          }
          return { ...img, reaction };
        })
      }));
    } catch (err) {
      console.error('Error submitting reaction:', err);
      showToast('Failed to save reaction');
    }
  };

  const handleComment = async (imageId, comment) => {
    if (board.is_locked) return;
    
    const image = board.images.find(img => img.id === imageId);
    const pid = board.shared_decision_mode ? selectedParticipant : undefined;
    const currentReaction = board.shared_decision_mode && pid != null
      ? (image?.reactions?.[String(pid)]?.reaction)
      : image?.reaction;
    
    if (!currentReaction) {
      showToast('Please select a reaction first');
      return;
    }
    
    try {
      await clientAPI.submitReaction(shareToken, imageId, currentReaction, comment, pid);
      setBoard(prev => ({
        ...prev,
        images: prev.images.map(img => {
          if (img.id !== imageId) return img;
          if (board.shared_decision_mode && pid != null) {
            const reactions = { ...(img.reactions || {}) };
            reactions[String(pid)] = { ...reactions[String(pid)], comment };
            return { ...img, reactions };
          }
          return { ...img, comment };
        })
      }));
    } catch (err) {
      console.error('Error saving comment:', err);
    }
  };

  const handleToggleFinal = async (imageId) => {
    try {
      const { data } = await clientAPI.toggleFinal(shareToken, imageId);
      setBoard(prev => ({
        ...prev,
        images: prev.images.map(img =>
          img.id === imageId ? { ...img, is_final: data.is_final } : img
        )
      }));
    } catch (err) {
      console.error('Error toggling final:', err);
    }
  };

  const inspirationBatch = useBatchUpload({
    uploadFn: async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return clientAPI.uploadInspiration(shareToken, formData);
    },
    concurrency: 5,
    onAllDone: () => {
      // Refresh the board so the new inspiration cards appear under the
      // gallery. The summary card stays visible so the client sees what
      // happened — they dismiss it themselves.
      fetchBoard();
    },
  });

  const onDrop = useCallback((acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;
    if (board.is_locked || !board.can_add_inspirations) return;

    // Pre-flight: the backend caps inspirations at 10 per board. If a
    // client drops 555 files, sending them all and watching 545 fail is a
    // bad experience — trim to the remaining slots up front and show a
    // calm message about what was clipped.
    const used = board.inspiration_count || 0;
    const remainingSlots = Math.max(0, 10 - used);
    if (remainingSlots === 0) {
      showToast("You've reached the maximum of 10 inspirations.");
      return;
    }
    const toUpload = acceptedFiles.slice(0, remainingSlots);
    const clipped = acceptedFiles.length - toUpload.length;
    if (clipped > 0) {
      showToast(`Only ${remainingSlots} more inspiration${remainingSlots > 1 ? 's' : ''} can fit on this board. ${clipped} skipped.`);
    }
    inspirationBatch.start(toUpload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareToken, board, inspirationBatch]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.heic', '.heif']
    },
    disabled: board?.is_locked || !board?.can_add_inspirations
  });

  const handleDeleteInspiration = async (inspirationId) => {
    if (board.is_locked) return;
    
    try {
      await clientAPI.deleteInspiration(shareToken, inspirationId);
      fetchBoard();
      showToast('Inspiration removed');
    } catch (err) {
      console.error('Error deleting inspiration:', err);
    }
  };

  const handleInspirationNote = async (inspirationId, note) => {
    if (board.is_locked) return;
    
    try {
      await clientAPI.updateInspirationNote(shareToken, inspirationId, note || null);
      setBoard(prev => ({
        ...prev,
        inspirations: prev.inspirations.map(insp => 
          insp.id === inspirationId ? { ...insp, note } : insp
        )
      }));
      showToast(note ? 'Note saved' : 'Note removed');
    } catch (err) {
      console.error('Error saving inspiration note:', err);
      showToast('Failed to save note');
    }
  };

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  };

  // Loading state
  const lang = board?.client_language || 'en';
  const t = getTranslation(lang);

  // ── Owner edit helpers ────────────────────────────────────────────
  const handleOwnerUpdate = async (updates) => {
    try {
      await boardsAPI.update(board.id, updates);
      await fetchBoard();
      showToast(t.saved || 'Saved');
    } catch (_e) {
      showToast(t.save_failed || 'Could not save');
    }
  };
  // Section texts require plan-level override permission; the save
  // itself flips `use_workspace_defaults` off so "click → edit → done"
  // needs no separate toggle hunting.
  const canEditSections = editMode && ownerFA?.allow_client_view_override === true;
  const canEditWelcome = editMode && ownerFA?.allow_custom_welcome === true;
  const lockedToast = () => showToast(
    t.cve_workspace_defaults_locked_hint || 'Available on Pro and Signature.'
  );
  const saveSection = (field) => (v) =>
    handleOwnerUpdate({ [field]: v, use_workspace_defaults: false });

  // Custom texts from board (per-language snapshot) + user branding overrides (per-language).
  // Priority per spec:
  //   if branding override has anything  → branding[lang] → template[lang] → template.en
  //   if no branding override            → template[lang] → template.en
  // Flat fallback (footer keys, custom_message, etc.) always reads from the shared
  // sibling on the branding.texts object.
  const templateTextsByLang = board?.custom_texts;           // snapshot stored on the board
  const brandingTextsByLang = board?.branding?.texts;        // user overrides (resolved)
  const brandTextsFlat = normalizeByLang(brandingTextsByLang);

  const brandName = board?.branding?.workspace_name || t.studio_name;
  const canBrand = board?.branding?.can_customize_branding || false;
  const clientTone = board?.branding?.client_tone || 'default';
  const clientCustomColor = board?.branding?.custom_color || '';

  // Shared (language-agnostic) fields — footer and custom message live flat on
  // branding.texts as siblings of the per-language buckets.
  const customFooterText = brandTextsFlat.custom_footer_text || '';
  const customFooterSubtext = brandTextsFlat.custom_footer_subtext || '';
  const customFooterLinkLabel = brandTextsFlat.custom_footer_link_label || '';
  const customFooterLinkUrl = brandTextsFlat.custom_footer_link_url || '';

  const pick = (key, fallback) => resolveText({
    branding: brandingTextsByLang,
    template: templateTextsByLang,
    lang,
    key,
    fallback,
  });

  // Board-Specific Welcome Override (Pro / Signature) — the highest
  // priority in the welcome-text chain:
  //   board.custom_welcome_*  →  branding override  →  template  →  system default
  // We only consult the board fields when the master toggle is on AND
  // the individual field has a non-empty value. Empty strings cleanly
  // fall back to the next tier so users can clear lines individually.
  const boardWelcomeActive = board?.use_custom_welcome === true;
  const boardWelcome = {
    title: boardWelcomeActive ? (board?.custom_welcome_title || '').trim() : '',
    description: boardWelcomeActive ? (board?.custom_welcome_description || '').trim() : '',
    hint: boardWelcomeActive ? (board?.custom_welcome_hint || '').trim() : '',
  };

  // Per-board section overrides only apply when the creator has
  // explicitly disabled "Use Workspace Defaults" in the CV Editor.
  // Basic-plan boards (and every legacy board) leave this True and
  // resolve through the workspace-level defaults exactly like before.
  const allowBoardOverride = board?.use_workspace_defaults === false;
  const boardOverride = (v) => allowBoardOverride ? (v || '').trim() : '';

  const txt = {
    welcome: boardWelcome.title || pick('welcome_title', t.welcome),
    client_intro: boardWelcome.description || pick('welcome_subtitle', t.client_intro),
    client_hint: boardWelcome.hint || pick('welcome_description', t.client_hint),
    // Section title/description resolution — board-level override (Client
    // View Editor) wins ONLY when the board has opted out of workspace
    // defaults; otherwise workspace default from user settings, then the
    // platform's translated fallback.
    // Gallery has no workspace-level default (it's the moodboard's
    // main grid — heading is only added if the creator wants one).
    gallery_title: boardOverride(board?.gallery_section_title),
    gallery_subtitle: boardOverride(board?.gallery_section_description),
    your_inspirations: boardOverride(board?.inspirations_section_title) || pick('inspiration_title', t.your_inspirations),
    inspiration_intro: boardOverride(board?.inspirations_section_description) || pick('inspiration_description', t.inspiration_intro),
    notes_title: boardOverride(board?.notes_section_title) || pick('notes_title', t.notes_title),
    notes_subtitle: boardOverride(board?.notes_section_description) || pick('notes_description', t.notes_subtitle),
    documents_title: boardOverride(board?.documents_section_title) || pick('documents_title', t.documents_title),
    documents_subtitle: boardOverride(board?.documents_section_description) || pick('documents_description', t.documents_subtitle),
    // Final Decision has no workspace-level default (it's a per-board
    // banner, not a repeating section) — fall straight through from
    // board override → translated fallback.
    final_decision_title: boardOverride(board?.final_decision_section_title) || (t.final_decision_section_title || ''),
    final_decision_subtitle: boardOverride(board?.final_decision_section_description) || (t.fd_share_notice || t.final_decision_prompt || ''),
    custom_message: pick('custom_message', ''),
  };

  if (loading) {
    return (
      <div className="client-view" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="expired-view">
        <div className="expired-icon">✕</div>
        <h1 className="expired-title">Board Not Found</h1>
        <p className="expired-message">{error}</p>
      </div>
    );
  }

  // Workspace unavailable (owner not active)
  if (board.workspace_unavailable) {
    return (
      <div className="expired-view">
        <div style={{ marginBottom: '48px', letterSpacing: '0.3em', textTransform: 'uppercase' }}>
          <PlatformFooter variant="compact" />
        </div>
        <h1 className="expired-title" data-testid="workspace-unavailable-title" style={{ fontSize: '24px', fontWeight: '400', marginBottom: '16px' }}>
          Workspace Unavailable
        </h1>
        <p className="expired-message" data-testid="workspace-unavailable-message" style={{ whiteSpace: 'pre-line', lineHeight: '1.8' }}>
          {board.message || 'This workspace is currently unavailable.\nPlease contact the creator for access.'}
        </p>
        <div style={{
          position: 'absolute', bottom: '32px', left: 0, right: 0, textAlign: 'center',
        }}>
          <PlatformFooter />
        </div>
      </div>
    );
  }

  // Board expired state — skipped entirely on Inspiration Boards.
  // An Inspiration Board is presentation-only, so the expired
  // "thank-you" screen (which acknowledges client choices and notes)
  // doesn't apply. We fall through and render the regular layout.
  if (board.expired && board?.board_type !== 'inspiration') {
    return (
      <div className="expired-view">
        <div style={{ marginBottom: '48px', letterSpacing: '0.3em', textTransform: 'uppercase' }}>
          <PlatformFooter variant="compact" />
        </div>
        <h1 className="expired-title">{t.expired_title}</h1>
        <p className="expired-message" style={{ marginTop: '24px' }}>
          {t.expired_message.split('\n').map((line, i) => <React.Fragment key={i}>{line}<br /></React.Fragment>)}
        </p>

        {/* Footer */}
        <div style={{
          position: 'absolute',
          bottom: '32px',
          left: 0,
          right: 0,
          textAlign: 'center'
        }}>
          <PlatformFooter />
        </div>
      </div>
    );
  }

  // Archived state — not accessible to clients
  if (board.archived) {
    return (
      <div className="expired-view">
        <div style={{ marginBottom: '48px', letterSpacing: '0.3em', textTransform: 'uppercase' }}>
          <PlatformFooter variant="compact" />
        </div>
        <h1 className="expired-title">{t.archived_title}</h1>
        <div style={{
          position: 'absolute',
          bottom: '32px',
          left: 0,
          right: 0,
          textAlign: 'center'
        }}>
          <PlatformFooter />
        </div>
      </div>
    );
  }

  // Only apply tone when user explicitly chose a non-default tone

  // Inspiration boards present a curated concept — no reactions,
  // conversations, decisions, notes, uploads, documents, welcome or
  // custom message. We compute this once and OR it into each existing
  // visibility check so the file's existing data-paths stay intact.
  // (When the owner converts the board to a Client Board, every flag
  // flips back to its previous value because no data was destroyed.)
  const isInspiration = board?.board_type === 'inspiration';

  // Shared decision: participant selection screen
  const participants = board.shared_decision_participants || ['Person 1', 'Person 2'];
  if (board.shared_decision_mode && !isInspiration && showParticipantPicker && !editMode) {
    return (
      <div className="expired-view" data-testid="participant-picker" style={{ background: '#faf8f5' }}>
        <p style={{
          fontFamily: 'Playfair Display, serif', fontSize: '12px',
          letterSpacing: '0.3em', textTransform: 'uppercase',
          color: 'var(--kahla-text-light)', marginBottom: '48px',
        }}>{ board?.branding?.workspace_name || 'MAESTRA' }</p>
        <h1 style={{
          fontFamily: 'Playfair Display, serif', fontSize: '24px',
          fontWeight: '400', color: 'var(--kahla-text)', marginBottom: '12px',
        }}>{t.who_are_you || 'Who are you?'}</h1>
        <p style={{ fontSize: '13px', color: 'var(--kahla-text-light)', marginBottom: '32px', lineHeight: '1.6' }}>
          {t.select_your_name || 'Select your name to start reviewing'}
        </p>
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
          {participants.map((name, idx) => (
            <button
              key={idx}
              data-testid={`pick-participant-${idx}`}
              onClick={() => selectParticipant(idx)}
              style={{
                padding: '16px 32px', fontSize: '15px', fontWeight: '500',
                fontFamily: 'Playfair Display, serif', letterSpacing: '0.03em',
                border: '1px solid var(--kahla-border)', background: 'var(--kahla-white)',
                color: 'var(--kahla-text)', cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.target.style.background = '#f0ede8'; }}
              onMouseLeave={e => { e.target.style.background = 'var(--kahla-white)'; }}
            >{name || `Person ${idx + 1}`}</button>
          ))}
        </div>
      </div>
    );
  }

  const isCustomTone = clientTone && clientTone !== 'default' && clientTone !== 'ivory';
  const toneOverrides = isCustomTone ? (() => {
    const tc = resolveTone(clientTone, clientCustomColor);
    return { backgroundColor: tc.page };
  })() : {};

  const coverActive = board?.board_cover_enabled && !coverDismissed && !board?.archived;

  return (
    <div className={`client-view fade-in${coverActive ? '' : ' cv-reveal'}`} style={toneOverrides}>
      {/* ── Owner Edit Mode banner — fixed above everything (incl. the
          cover overlay) so the creator always sees where they are and
          how to leave. */}
      {editMode && (
        <>
          <div
            data-testid="cv-edit-mode-banner"
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, zIndex: 3000,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: '16px', padding: '10px 20px',
              background: 'var(--kahla-charcoal, #2a2620)', color: '#f6f1e7',
              fontFamily: 'Playfair Display, serif',
              fontSize: '11px', letterSpacing: '0.18em', textTransform: 'uppercase',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span aria-hidden="true">✦</span>
              <span>{t.cv_edit_mode_banner || 'Editing client view — click any text on the page'}</span>
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                data-testid="cv-edit-advanced-btn"
                style={{
                  background: showAdvanced ? 'rgba(246,241,231,0.15)' : 'transparent',
                  border: '1px solid rgba(246,241,231,0.35)',
                  color: '#f6f1e7', padding: '6px 14px', cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: '10px',
                  letterSpacing: '0.18em', textTransform: 'uppercase',
                }}
              >{t.inline_advanced_options || 'Advanced ▾'}</button>
              <button
                type="button"
                onClick={() => navigate(`/editor/${board.id}`)}
                data-testid="cv-edit-done-btn"
                style={{
                  background: '#f6f1e7', color: 'var(--kahla-charcoal)',
                  border: 'none', padding: '6px 18px', cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: '10px',
                  letterSpacing: '0.18em', textTransform: 'uppercase',
                }}
              >{t.cv_edit_done || 'Done'}</button>
            </span>
          </div>
          <div style={{ height: '44px' }} aria-hidden="true" />
          {showAdvanced && (
            <div
              data-testid="cv-edit-advanced-panel"
              style={{
                position: 'fixed', top: '40px', right: 0, bottom: 0,
                width: 'min(420px, 92vw)', overflowY: 'auto', zIndex: 2990,
                background: 'var(--kahla-cream-light, #faf8f5)',
                borderLeft: '1px solid var(--kahla-border, #e6dfd3)',
                boxShadow: '-12px 0 40px rgba(42,38,32,0.10)',
                padding: '24px 20px 48px',
              }}
            >
              <ClientViewEditorSidebar
                t={t}
                board={board}
                featureAccess={ownerFA || {}}
                handleUpdateSettings={handleOwnerUpdate}
                onClose={() => setShowAdvanced(false)}
                onPreviewToggle={() => {}}
                previewAsClient={false}
                hidePreviewToggle
              />
            </div>
          )}
        </>
      )}
      {/* Board Cover Overlay — full-screen cinematic entrance. Mounted
          above the regular Client View. The view itself is fully
          rendered underneath so the moment the client taps "Enter",
          the cover fades and the moodboard is already live (no
          loading flash). */}
      {/* Board Cover Overlay — render whenever enabled, including on
          locked boards. The cover is part of the studio's curated
          arrival experience; suppressing it when locked would hide a
          finished, intentional moment. The locked banner still appears
          underneath once the client enters. Archived boards skip the
          cover so the read-only/expired notice surfaces immediately. */}
      {board?.board_cover_enabled && !coverDismissed && !board?.archived && (
        <BoardCoverOverlay
          board={board}
          brandName={(board?.branding?.workspace_name || '').trim()}
          brandLogoUrl={
            board?.branding?.show_logo_in_client && board?.branding?.workspace_logo_url
              ? board.branding.workspace_logo_url
              : ''
          }
          t={t}
          ownerEdit={editMode ? { active: true, save: handleOwnerUpdate, t } : null}
          onEnter={() => {
            setCoverDismissed(true);
            if (board?.board_cover_first_visit_only && !editMode) {
              try { localStorage.setItem(`maestra_cover_seen_${shareToken}`, '1'); } catch (e) {}
            }
          }}
        />
      )}
      {/* Meta tags for privacy */}
      <meta name="robots" content="noindex, nofollow" />
      
      {/* Header */}
      <header className="client-header">
        {(() => {
          const branding = board?.branding || {};
          const displayMode = branding.branding_display_mode || 'logo_and_name';
          const size = branding.logo_size || 'M';
          const sizeMap = { S: '48px', M: '80px', L: '260px' };
          const maxWidthMap = { S: '220px', M: '360px', L: '780px' };
          const hasLogo = branding.show_logo_in_client && branding.workspace_logo_url;
          const showLogo = hasLogo && displayMode !== 'name_only';
          const showName = displayMode !== 'logo_only' || !hasLogo;

          return (
            <div style={{ marginBottom: '96px' }}>
              {showLogo && (
                <div style={{ marginBottom: showName ? '10px' : '0' }}>
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/images/${branding.workspace_logo_url}`}
                    alt={brandName}
                    data-testid="client-logo-img"
                    style={{
                      height: 'auto',
                      minHeight: sizeMap[size],
                      maxHeight: sizeMap[size],
                      maxWidth: maxWidthMap[size],
                      objectFit: 'contain',
                      display: 'block',
                      margin: '0 auto',
                    }}
                    onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                  />
                </div>
              )}
              {showName && (
                <p style={{
                  fontFamily: 'Playfair Display, serif',
                  fontSize: showLogo ? '11px' : '12px',
                  letterSpacing: '0.3em',
                  textTransform: 'uppercase',
                  color: 'var(--kahla-text-light)',
                  margin: 0,
                }}>{brandName}</p>
              )}
            </div>
          );
        })()}
        {/* Optional board title — surfaces the board's own name above the
            generic welcome. Off by default for backward compatibility; the
            owner enables this per-board in Board Settings → Client view
            header. Editorial typography matches the existing rhythm:
            Playfair Display 24px (clamps down on mobile), 0.04em tracking,
            charcoal — sits between the logo / brand name and the welcome
            headline so it reads as a chapter marker, not a UI label. */}
        {/* Optional board title — sits above the welcome. Larger than
            the welcome `<h1>` (which stays at 36px) so it clearly leads
            the hierarchy as the page's chapter heading, while "Witaj."
            remains as the friendly greeting underneath. Hidden entirely
            when the owner hasn't opted in. */}
        {/* Inspiration Boards always render their title + concept
            description as the page's chapter heading — the surface IS
            the curated presentation. Client Boards no longer show a
            separate board-title header: the Welcome section is the
            single introduction (the "Client view header" option was
            removed to reduce intro clutter). */}
        {isInspiration && board.name && (
          <>
            <h2
              data-testid="client-board-title"
              style={{
                fontFamily: '"Playfair Display", serif',
                // Triple-scale editorial heading — clearly dominant
                // typography that introduces the chapter, with smooth
                // clamp so phones get a still-large but readable size.
                fontSize: 'clamp(48px, 9vw, 96px)',
                letterSpacing: '-0.005em',
                color: 'var(--kahla-charcoal)',
                textAlign: 'center',
                // Match the owner-side `.board-top__title` weight (400)
                // for a refined editorial feel — 500 reads as bold at
                // this scale and felt heavy against the rest of the
                // page typography.
                fontWeight: 400,
                lineHeight: 1.08,
                margin: board.board_subtitle ? '0 auto 16px' : '0 auto 120px',
                maxWidth: '900px',
                padding: '0 16px',
              }}
            >
              {board.name}
            </h2>
            {(board.board_subtitle || editMode) && (
              <OwnerInlineText
                edit={editMode}
                as="p"
                multi
                value={board.board_subtitle}
                onSave={(v) => handleOwnerUpdate({ board_subtitle: v })}
                placeholder={t.board_subtitle_placeholder}
                testId="client-board-subtitle"
                style={{
                  // Cormorant italic deck between the title and "Witaj."
                  // — reads as editorial counter-type, never competes
                  // with the heading or the welcome.
                  fontFamily: '"Cormorant Garamond", "Playfair Display", serif',
                  fontStyle: 'italic',
                  fontSize: 'clamp(15px, 2.4vw, 20px)',
                  letterSpacing: '0.02em',
                  color: 'var(--kahla-taupe, var(--kahla-text-light))',
                  textAlign: 'center',
                  fontWeight: 400,
                  lineHeight: 1.4,
                  margin: '0 auto 120px',
                  maxWidth: '600px',
                  padding: '0 16px',
                }}
              />
            )}
          </>
        )}
        {!isInspiration && (
          <>
            <OwnerInlineText
              edit={editMode}
              as="h1"
              className="client-board-name"
              value={txt.welcome}
              onSave={(v) => handleOwnerUpdate({ custom_welcome_title: v, use_custom_welcome: true })}
              placeholder={t.custom_welcome_title_placeholder}
              locked={editMode && !canEditWelcome}
              onLockedClick={lockedToast}
              testId="cv-welcome-title"
            />
            <OwnerInlineText
              edit={editMode}
              as="p"
              multi
              value={txt.client_intro}
              onSave={(v) => handleOwnerUpdate({ custom_welcome_description: v, use_custom_welcome: true })}
              placeholder={t.custom_welcome_description_placeholder}
              locked={editMode && !canEditWelcome}
              onLockedClick={lockedToast}
              testId="cv-welcome-description"
              style={{
              fontSize: '15px',
              color: 'var(--kahla-text-light)',
              maxWidth: '500px',
              margin: '16px auto 0',
              lineHeight: '1.7'
            }}>
              {txt.client_intro.split('\n').map((line, i) => <React.Fragment key={i}>{line}<br /></React.Fragment>)}
            </OwnerInlineText>
            <OwnerInlineText
              edit={editMode}
              as="p"
              value={txt.client_hint}
              onSave={(v) => handleOwnerUpdate({ custom_welcome_hint: v, use_custom_welcome: true })}
              placeholder={t.custom_welcome_hint_placeholder}
              locked={editMode && !canEditWelcome}
              onLockedClick={lockedToast}
              testId="cv-welcome-hint"
              style={{
              fontSize: '13px',
              color: 'var(--kahla-text-light)',
              fontStyle: 'italic',
              marginTop: '24px',
              opacity: 0.8
            }} />
            {txt.custom_message && (
              <p data-testid="client-custom-message" style={{
                fontSize: '14px',
                color: 'var(--kahla-text)',
                maxWidth: '500px',
                margin: '20px auto 0',
                lineHeight: '1.7',
                padding: '16px 20px',
                backgroundColor: 'var(--kahla-cream-light)',
                border: '1px solid var(--kahla-border)',
                whiteSpace: 'pre-wrap',
              }}>
                {txt.custom_message}
              </p>
            )}
          </>
        )}

        {/* Shared decision: participant indicator */}
        {!isInspiration && board.shared_decision_mode && selectedParticipant != null && (
          <div data-testid="participant-indicator" style={{
            display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px',
            marginTop: '20px', padding: '8px 16px',
            border: '1px solid var(--kahla-border)', background: 'var(--kahla-white)',
            width: 'fit-content', margin: '20px auto 0',
          }}>
            <span style={{ fontSize: '12px', color: 'var(--kahla-text-light)' }}>Viewing as</span>
            <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--kahla-text)' }}>
              {participants[selectedParticipant] || `Person ${selectedParticipant + 1}`}
            </span>
            <button
              data-testid="switch-participant-btn"
              onClick={() => setShowParticipantPicker(true)}
              style={{
                padding: '3px 10px', fontSize: '11px', letterSpacing: '0.03em',
                border: '1px solid var(--kahla-border)', background: 'transparent',
                color: 'var(--kahla-text-light)', cursor: 'pointer', fontFamily: 'inherit',
              }}
            >Switch</button>
          </div>
        )}
      </header>

      {/* Locked Banner — hidden on Inspiration Boards. They are
          read-only by design (no reactions/comments/uploads) so the
          "this moodboard is closed" framing doesn't apply. */}
      {board.is_locked && !isInspiration && (
        <div className="locked-banner" style={{ maxWidth: '600px', margin: '0 auto 32px' }}>
          <p style={{ fontWeight: '500', marginBottom: '8px' }}>
            {t.locked_title.split('\n').map((line, i) => <React.Fragment key={i}>{line}<br /></React.Fragment>)}
          </p>
          <p style={{ fontSize: '13px', opacity: 0.85, lineHeight: 1.55 }}>
            {t.locked_subtitle.split('\n').map((line, i) => <React.Fragment key={i}>{line}<br /></React.Fragment>)}
          </p>
          {t.locked_helper && (
            <p
              data-testid="locked-helper"
              style={{
                fontSize: '12px',
                opacity: 0.6,
                marginTop: '12px',
                fontStyle: 'italic',
              }}
            >
              {t.locked_helper}
            </p>
          )}
        </div>
      )}

      {/* Main Content */}
      <main
        className="client-content"
        style={pageNarrow ? { paddingBottom: 'calc(72px + env(safe-area-inset-bottom))' } : undefined}
      >
        {/* Instruction Box removed — the Welcome section (title +
            description + italic hint) is the single introduction the
            client reads. Page flow: Welcome → Gallery → Inspirations →
            Conversation → Documents → Final Selections. */}

        {/* Final Decision Mode notice — share-link viewers see this message
            informing them that final selections happen privately via secure
            email invitation. The final-toggle UI is intentionally hidden. */}
        {!isInspiration && board.final_decision_mode && (
          <div style={{
            maxWidth: '500px',
            margin: '0 auto 40px',
            textAlign: 'center',
            padding: '20px 28px',
            border: '1px solid rgba(201,169,110,0.3)',
            backgroundColor: 'rgba(201,169,110,0.04)',
          }}
          data-testid="final-decision-banner"
          >
            {(txt.final_decision_title || '').trim() && (
              <h2 style={{
                fontFamily: 'Playfair Display, serif',
                fontSize: '18px',
                color: 'var(--kahla-charcoal)',
                margin: '0 0 10px',
                letterSpacing: '0.02em',
              }}
              data-testid="final-decision-heading"
              >
                {txt.final_decision_title}
              </h2>
            )}
            <p style={{
              fontSize: '13px',
              color: 'var(--kahla-text-light, #8a847b)',
              fontStyle: 'italic',
              letterSpacing: '0.02em',
              lineHeight: '1.7',
              margin: 0,
            }}
            data-testid="final-decision-subtitle"
            >
              {txt.final_decision_subtitle}
            </p>
          </div>
        )}

        {/* Sections rendered in template-defined order. Hidden sections are
            skipped; missing keys fall back to canonical sequence. */}
        {(() => {
          const canonical = ['gallery', 'client_uploads', 'notes', 'documents'];
          const orderRaw = Array.isArray(board.section_order) && board.section_order.length
            ? board.section_order
            : canonical.map(k => ({ key: k, visible: true }));
          // Filter to only the client-visible sections we actually render here.
          const order = orderRaw
            .filter(s => canonical.includes(s.key))
            .filter(s => s.visible !== false);
          // Append any canonical key that was somehow missing (defensive).
          for (const k of canonical) {
            if (!order.some(s => s.key === k)) order.push({ key: k, visible: true });
          }

          const renderSection = (key) => {
            switch (key) {
              case 'gallery':
                if (board.hide_gallery === true) return null;
                return (
                  <section
                    key="gallery"
                    id="client-section-gallery"
                    data-section="gallery"
                    data-testid="client-section-gallery"
                  >
                    {(editMode || txt.gallery_title || txt.gallery_subtitle) && (
                      <div style={{ maxWidth: 700, margin: '0 auto 32px', textAlign: 'center' }}
                           data-testid="client-gallery-heading">
                        {(editMode || txt.gallery_title) && (
                          <OwnerInlineText
                            edit={editMode}
                            as="h2"
                            className="client-section-title"
                            value={txt.gallery_title}
                            onSave={saveSection('gallery_section_title')}
                            placeholder={t.cve_gallery_title_placeholder || 'Optional heading above the images'}
                            locked={editMode && !canEditSections}
                            onLockedClick={lockedToast}
                            testId="cv-gallery-title"
                            style={{
                            fontFamily: 'Playfair Display, serif',
                            fontSize: '24px',
                            color: 'var(--kahla-charcoal)',
                            marginBottom: txt.gallery_subtitle ? '12px' : '0',
                          }} />
                        )}
                        {(editMode || txt.gallery_subtitle) && (
                          <OwnerInlineText
                            edit={editMode}
                            as="p"
                            multi
                            value={txt.gallery_subtitle}
                            onSave={saveSection('gallery_section_description')}
                            placeholder={t.cve_gallery_description_placeholder || 'Optional context — a short line under the heading'}
                            locked={editMode && !canEditSections}
                            onLockedClick={lockedToast}
                            testId="cv-gallery-subtitle"
                            style={{
                            fontSize: '13px',
                            color: 'var(--kahla-text-light)',
                            fontStyle: 'italic',
                            lineHeight: 1.6,
                            margin: 0,
                          }} />
                        )}
                      </div>
                    )}
                    {/* Gallery Sections rendering (Etap 5) — if the
                        board has any gallery_sections defined, we
                        partition the images into an implicit
                        uncategorized bucket + one grid per section
                        with the section title/description above.
                        Empty gallery_sections falls back to the
                        legacy flat grid so nothing changes for
                        existing boards. */}
                    {(() => {
                      const gs = Array.isArray(board.gallery_sections) ? board.gallery_sections : [];
                      if (gs.length === 0) {
                        return (
                          <div className="moodboard-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
                            {board.images?.map((image, index) => (
                              <ImageCard
                                key={image.id}
                                image={image}
                                isLocked={board.is_locked || isInspiration}
                                onReaction={isInspiration ? null : (reaction) => handleReaction(image.id, reaction)}
                                onComment={isInspiration ? null : (comment) => handleComment(image.id, comment)}
                                onImageClick={() => setLightboxIndex(index)}
                                finalDecisionMode={false}
                                onToggleFinal={handleToggleFinal}
                                t={t}
                                sharedMode={!isInspiration && board.shared_decision_mode}
                                participantId={selectedParticipant}
                                participants={participants}
                                brandName={brandName}
                                allowImageConversations={!isInspiration && (board.allow_image_conversations !== false)}
                                reactionMode={board.reaction_mode || 'mood'}
                              />
                            ))}
                          </div>
                        );
                      }
                      const ordered = [...gs].sort((a, b) => (a.order || 0) - (b.order || 0));
                      const knownIds = new Set(ordered.map((s) => s.id));
                      const uncategorized = (board.images || []).filter((img) => !img.gallery_section_id || !knownIds.has(img.gallery_section_id));
                      const renderCard = (image) => {
                        const idx = board.images.findIndex((im) => im.id === image.id);
                        return (
                          <ImageCard
                            key={image.id}
                            image={image}
                            isLocked={board.is_locked || isInspiration}
                            onReaction={isInspiration ? null : (reaction) => handleReaction(image.id, reaction)}
                            onComment={isInspiration ? null : (comment) => handleComment(image.id, comment)}
                            onImageClick={() => setLightboxIndex(idx)}
                            finalDecisionMode={false}
                            onToggleFinal={handleToggleFinal}
                            t={t}
                            sharedMode={!isInspiration && board.shared_decision_mode}
                            participantId={selectedParticipant}
                            participants={participants}
                            brandName={brandName}
                            allowImageConversations={!isInspiration && (board.allow_image_conversations !== false)}
                            reactionMode={board.reaction_mode || 'mood'}
                          />
                        );
                      };
                      return (
                        <>
                          {uncategorized.length > 0 && (
                            <div className="moodboard-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}
                                 data-testid="client-gallery-uncategorized">
                              {uncategorized.map(renderCard)}
                            </div>
                          )}
                          {ordered.map((s) => {
                            const imgs = (board.images || []).filter((im) => im.gallery_section_id === s.id);
                            if (imgs.length === 0) return null;
                            return (
                              <div key={s.id} style={{ marginTop: '48px' }} data-testid={`client-gallery-section-${s.id}`}>
                                <div style={{ maxWidth: '700px', margin: '0 auto 24px', textAlign: 'center' }}>
                                  <h3 style={{
                                    fontFamily: 'Playfair Display, serif',
                                    fontSize: '18px', letterSpacing: '0.24em',
                                    textTransform: 'uppercase', color: 'var(--kahla-charcoal)',
                                    margin: 0, fontWeight: 500,
                                  }}>{s.title}</h3>
                                  {s.description && (
                                    <p style={{
                                      margin: '8px 0 0',
                                      fontFamily: 'Cormorant Garamond, Playfair Display, serif',
                                      fontStyle: 'italic', fontSize: '14px',
                                      color: 'var(--kahla-text-light)', lineHeight: 1.6,
                                    }}>{s.description}</p>
                                  )}
                                </div>
                                <div className="moodboard-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
                                  {imgs.map(renderCard)}
                                </div>
                              </div>
                            );
                          })}
                        </>
                      );
                    })()}
                  </section>
                );
              case 'client_uploads':
                if (isInspiration || board.allow_client_uploads === false) return null;
                return (
                  <div
                    key="client_uploads"
                    id="client-section-client_uploads"
                    data-section="client_uploads"
                    data-testid="client-section-client_uploads"
                    className="inspiration-section"
                  >
                    <OwnerInlineText
                      edit={editMode}
                      as="h2"
                      className="client-section-title"
                      value={txt.your_inspirations}
                      onSave={saveSection('inspirations_section_title')}
                      placeholder={t.your_inspirations}
                      locked={editMode && !canEditSections}
                      onLockedClick={lockedToast}
                      testId="cv-inspirations-title"
                    />
                    <OwnerInlineText
                      edit={editMode}
                      as="p"
                      multi
                      value={txt.inspiration_intro}
                      onSave={saveSection('inspirations_section_description')}
                      placeholder={t.inspiration_intro}
                      locked={editMode && !canEditSections}
                      onLockedClick={lockedToast}
                      testId="cv-inspirations-intro"
                      style={{
                        textAlign: 'center', fontSize: '14px',
                        color: 'var(--kahla-text-light)', maxWidth: '500px',
                        margin: '0 auto 16px', lineHeight: '1.7',
                      }}
                    >
                      {txt.inspiration_intro.split('\n').map((line, i) => <React.Fragment key={i}>{line}<br /></React.Fragment>)}
                    </OwnerInlineText>
                    <p style={{
                      textAlign: 'center', fontSize: '12px',
                      color: 'var(--kahla-text-light)', fontStyle: 'italic',
                      marginBottom: '32px', opacity: 0.8
                    }}>
                      {t.inspiration_hint}
                    </p>
                    {!board.is_locked && board.can_add_inspirations && (
                      <>
                        <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                          <BatchUploadStatus
                            state={inspirationBatch.state}
                            label="inspiration"
                            onRetryFailed={inspirationBatch.retryFailed}
                            onDismiss={inspirationBatch.dismiss}
                            compact
                          />
                        </div>
                        <div
                          {...getRootProps()}
                          className={`dropzone ${isDragActive ? 'active' : ''}`}
                          style={{ marginBottom: '24px', padding: '32px', maxWidth: '500px', margin: '0 auto 24px' }}
                        >
                          <input {...getInputProps()} />
                          {(inspirationBatch.state.uploading || uploading) ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                              <div className="spinner" style={{ width: '24px', height: '24px' }}></div>
                              <span>{t.uploading || 'Uploading...'}</span>
                            </div>
                          ) : isDragActive ? (
                            <p>{t.drop_images_here || t.drop_to_upload || 'Drop images here...'}</p>
                          ) : (
                            <div>
                              <button className="btn-secondary" style={{ pointerEvents: 'none' }}>
                                {t.add_inspiration}
                              </button>
                              <p style={{ fontSize: '12px', color: 'var(--kahla-text-light)', marginTop: '12px' }}>
                                {board.inspiration_count}/10 {t.images_added}
                              </p>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    {!board.is_locked && !board.can_add_inspirations && board.inspiration_count >= 10 && (
                      <div style={{
                        textAlign: 'center', padding: '24px',
                        backgroundColor: 'var(--kahla-cream)',
                        border: '1px solid var(--kahla-border)',
                        maxWidth: '500px', margin: '0 auto 24px'
                      }}>
                        <p style={{ fontSize: '14px', color: 'var(--kahla-text)' }}>{t.max_inspirations}</p>
                        <p style={{ fontSize: '12px', color: 'var(--kahla-text-light)', marginTop: '8px' }}>
                          {t.max_inspirations_hint}
                        </p>
                      </div>
                    )}
                    {board.inspirations?.length > 0 && (
                      <div className="inspiration-grid" style={{ maxWidth: '800px', margin: '0 auto' }}>
                        {board.inspirations.map((insp, idx) => (
                          <InspirationCard
                            key={insp.id}
                            inspiration={insp}
                            isLocked={board.is_locked}
                            onDelete={() => handleDeleteInspiration(insp.id)}
                            onNoteChange={(note) => handleInspirationNote(insp.id, note)}
                            onImageClick={() => setInspirationLightboxIndex(idx)}
                          />
                        ))}
                      </div>
                    )}
                    {board.inspirations?.length === 0 && board.is_locked && (
                      <p style={{ textAlign: 'center', color: 'var(--kahla-text-light)', fontStyle: 'italic' }}>
                        {t.no_inspirations}
                      </p>
                    )}
                  </div>
                );
              case 'notes': {
                if (isInspiration || board.allow_notes === false) return null;
                // Conversation + Documents side-by-side on desktop
                // (50/50), stacked on mobile — keeps the bottom of the
                // page short without burying either section.
                const docsBeside = board.allow_documents !== false && (board.documents?.length > 0);
                const notesBlock = (
                  <div
                    key="notes"
                    id="client-section-notes"
                    data-section="notes"
                    data-testid="client-section-notes"
                  >
                    <NotesSection
                      notes={board.notes || []}
                      isLocked={board.is_locked}
                      canAddNotes={board.can_add_notes !== false}
                      shareToken={shareToken}
                      onNoteAdded={fetchBoard}
                      t={t}
                      txt={txt}
                      documents={board.documents || []}
                      images={board.images || []}
                      ownerEdit={editMode ? { locked: !canEditSections, save: handleOwnerUpdate, onLockedClick: lockedToast } : null}
                      brandName={brandName}
                      clientName={board.client_name || ''}
                      participants={board.shared_decision_mode ? (board.shared_decision_participants || []) : []}
                      sharedDecisionMode={!!board.shared_decision_mode}
                      selectedParticipant={selectedParticipant}
                    />
                  </div>
                );
                if (!docsBeside) return notesBlock;
                return (
                  <div key="notes-docs" className="cv-bottom-grid" data-testid="cv-bottom-grid">
                    {notesBlock}
                    <div
                      key="documents"
                      id="client-section-documents"
                      data-section="documents"
                      data-testid="client-section-documents"
                    >
                      <ClientDocumentsSection
                        documents={board.documents}
                        shareToken={shareToken}
                        t={t}
                        txt={txt}
                        ownerEdit={editMode ? { locked: !canEditSections, save: handleOwnerUpdate, onLockedClick: lockedToast } : null}
                      />
                    </div>
                  </div>
                );
              }
              case 'documents':
                if (isInspiration || board.allow_documents === false) return null;
                if (!(board.documents?.length > 0)) return null;
                // Already rendered beside the conversation when notes
                // are visible — avoid a duplicate standalone section.
                if (board.allow_notes !== false) return null;
                return (
                  <div
                    key="documents"
                    id="client-section-documents"
                    data-section="documents"
                    data-testid="client-section-documents"
                  >
                    <ClientDocumentsSection
                      documents={board.documents}
                      shareToken={shareToken}
                      t={t}
                      txt={txt}
                      ownerEdit={editMode ? { locked: !canEditSections, save: handleOwnerUpdate, onLockedClick: lockedToast } : null}
                    />
                  </div>
                );
              default:
                return null;
            }
          };

          return order.map(s => renderSection(s.key));
        })()}
      </main>

      {/* Mobile sticky bottom nav — fixed at the viewport bottom, only
          renders below 760px AND only with the tabs that actually have
          a target section on this board. Desktop is untouched. */}
      {pageNarrow && !isInspiration && (() => {
        const tabs = [];
        // Gallery / Final picks — always present
        tabs.push({
          key: 'gallery',
          label: board.final_decision_mode ? t.nav_final_decisions : t.nav_gallery,
          icon: board.final_decision_mode ? ICON_FINAL : ICON_GALLERY,
        });
        // Notes — only if enabled on this board
        if (board.allow_notes !== false) {
          tabs.push({ key: 'notes', label: t.nav_notes, icon: ICON_NOTES });
        }
        return (
          <MobileBottomNav
            tabs={tabs}
            activeKey={activeSection}
            onSelect={scrollToSection}
            visible={navVisible}
          />
        );
      })()}

      {/* Footer */}
      <footer style={{
        padding: '48px 24px',
        textAlign: 'center',
        borderTop: '1px solid var(--kahla-border)',
        backgroundColor: 'var(--kahla-cream)',
        marginTop: '64px'
      }}>
        {canBrand ? (
          /* A. Branding enabled → custom footer (if any) + subtle "created with maestra.space" */
          <>
            {customFooterText && (
              <p data-testid="custom-footer-text" style={{
                fontFamily: 'Playfair Display, serif',
                fontSize: '13px',
                letterSpacing: '0.12em',
                color: 'var(--kahla-text-light)',
                marginBottom: '4px',
              }}>
                {customFooterText}
              </p>
            )}
            {customFooterSubtext && (
              <p data-testid="custom-footer-subtext" style={{
                fontSize: '11px',
                letterSpacing: '0.08em',
                color: 'var(--kahla-text-light)',
                opacity: 0.7,
                marginBottom: '4px',
              }}>
                {customFooterSubtext}
              </p>
            )}
            {customFooterLinkLabel && customFooterLinkUrl && (
              <a
                href={customFooterLinkUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="custom-footer-link"
                style={{
                  fontSize: '11px',
                  color: 'var(--kahla-text-light)',
                  textDecoration: 'underline',
                  opacity: 0.7,
                  display: 'inline-block',
                  marginBottom: '12px',
                }}
              >
                {customFooterLinkLabel}
              </a>
            )}
            <p data-testid="maestra-attribution" style={{
              fontSize: '10px',
              letterSpacing: '0.08em',
              color: 'var(--kahla-text-light)',
              opacity: 0.55,
              marginTop: customFooterText ? '16px' : '0',
            }}>
              Created with Maestra
            </p>
          </>
        ) : (
          /* B. Branding disabled → platform footer from admin settings, no attribution */
          <>
            <p data-testid="platform-footer-text" style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: '12px',
              letterSpacing: '0.15em',
              color: 'var(--kahla-text-light)',
              marginBottom: '4px',
            }}>
              {ptx?.footer_text || 'MAESTRA 2026'}
            </p>
            <p data-testid="platform-footer-subtext" style={{
              fontSize: '10px',
              letterSpacing: '0.1em',
              color: 'var(--kahla-text-light)',
              opacity: 0.7,
            }}>
              {ptx?.footer_subtext || 'One project. One space. One clear direction.'}
            </p>
            {ptx?.footer_link && ptx?.footer_link_label && (
              <a
                href={ptx.footer_link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="platform-footer-link"
                style={{
                  fontSize: '10px',
                  color: 'var(--kahla-text-light)',
                  marginTop: '6px',
                  display: 'inline-block',
                  textDecoration: 'underline',
                  opacity: 0.7,
                }}
              >
                {ptx.footer_link_label}
              </a>
            )}
          </>
        )}
      </footer>

      {/* Image Lightbox */}
      {lightboxIndex !== null && board.images?.length > 0 && (
        <ImageLightbox
          images={board.images}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          // Inline reactions inside the lightbox — same handler as the grid view
          // so reaction state stays naturally synced across both surfaces. Skipped
          // when the board is locked so clients can't react after the studio
          // has closed the round, and on Inspiration Boards where reactions
          // don't exist as a surface at all.
          onReact={(board.is_locked || isInspiration) ? null : (image, value) => handleReaction(image.id, value)}
          reactionMode={board.reaction_mode || 'mood'}
          currentReactionFor={(image) => {
            if (board.shared_decision_mode && selectedParticipant != null) {
              return image.reactions?.[String(selectedParticipant)]?.reaction || null;
            }
            return image.reaction || null;
          }}
          t={t}
        />
      )}

      {/* Inspiration Lightbox */}
      {inspirationLightboxIndex !== null && board.inspirations?.length > 0 && (
        <ImageLightbox
          images={board.inspirations.map(insp => ({
            id: insp.id,
            image_full: insp.image_data,
            image_data: insp.image_data,
            image_thumb: insp.image_data,
            comment: insp.note || null,
            section_label: 'Your Inspiration',
          }))}
          initialIndex={inspirationLightboxIndex}
          onClose={() => setInspirationLightboxIndex(null)}
        />
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
};

// Notes Section Component
const NotesSection = ({ notes, isLocked, canAddNotes = true, shareToken, onNoteAdded, t, txt, ownerEdit = null, brandName, clientName = '', participants = [], sharedDecisionMode = false, selectedParticipant = null, documents = [], images = [] }) => {
  const [newNote, setNewNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Auto-scroll the just-added note into view — only when count grows.
  const notesEndRef = useRef(null);
  // Ref on the Add Note textarea so the small mobile "Add a note ↓"
  // pill can jump straight to the input and pop the keyboard, instead
  // of forcing the client to scroll past every prior note. The pill
  // only appears on narrow screens AND only when there are enough
  // notes to actually require scrolling.
  const textareaRef = useRef(null);
  const composerRef = useRef(null);
  // Ref on the outer section so we can detect when the user is
  // actually within the Notes panel (vs scrolled past it or above it).
  const sectionRef = useRef(null);
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 760 : false
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsNarrow(window.innerWidth < 760);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Mobile-only floating "+ new note" affordance.
  //
  // Show when the user is reading inside the Notes section but the
  // composer is below the fold — typical when there are several
  // existing notes. Hide the moment the composer scrolls into view
  // so the floating element doesn't shout over the actual input.
  const [showFloatingAddNote, setShowFloatingAddNote] = useState(false);
  useEffect(() => {
    if (!isNarrow || !canAddNotes) return;
    if (!sectionRef.current || !composerRef.current) return;
    let sectionVisible = false;
    let composerVisible = false;
    const update = () => setShowFloatingAddNote(sectionVisible && !composerVisible);
    const sectionObs = new IntersectionObserver((entries) => {
      sectionVisible = entries[0].isIntersecting;
      update();
    }, { threshold: 0 });
    const composerObs = new IntersectionObserver((entries) => {
      // Treat the composer as "visible" the moment any of it enters
      // the viewport — even a sliver — so the floating pill steps
      // out of the way well before the user reaches it.
      composerVisible = entries[0].isIntersecting;
      update();
    }, { threshold: 0, rootMargin: '0px 0px -80px 0px' });
    sectionObs.observe(sectionRef.current);
    composerObs.observe(composerRef.current);
    return () => {
      sectionObs.disconnect();
      composerObs.disconnect();
    };
  }, [isNarrow, canAddNotes, notes.length]);

  const jumpToComposer = useCallback(() => {
    if (composerRef.current) {
      composerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // Focus after the smooth-scroll has had a chance to start so iOS
    // Safari brings up the keyboard reliably.
    setTimeout(() => { textareaRef.current?.focus(); }, 320);
  }, []);
  const prevNotesCountRef = useRef(notes.length);
  const notesListRef = useRef(null);
  // "Seen" receipts — when the client actually views the conversation,
  // mark the studio's messages as seen (fires once per mount).
  const seenSentRef = useRef(null);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || seenSentRef.current) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !seenSentRef.current) {
        seenSentRef.current = true;
        clientAPI.markNotesSeen(shareToken).catch(() => {});
        obs.disconnect();
      }
    }, { threshold: 0.2 });
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareToken]);
  const lastClientNote = [...notes].reverse().find((n) => n.author_type === 'client');
  // Chat-style: the list scrolls INTERNALLY (fixed max height), so the
  // page never grows with the conversation. Start pinned to the newest
  // message, and glide down when a new one arrives.
  useEffect(() => {
    const el = notesListRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const el = notesListRef.current;
    if (notes.length > prevNotesCountRef.current && el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
    prevNotesCountRef.current = notes.length;
  }, [notes.length]);

  const handleSubmit = async () => {
    if (!newNote.trim() || !canAddNotes) return;
    
    setSubmitting(true);
    try {
      const pIdx = (sharedDecisionMode && typeof selectedParticipant === 'number') ? selectedParticipant : null;
      await clientAPI.addNote(shareToken, newNote.trim(), pIdx);
      setNewNote('');
      onNoteAdded();
    } catch (err) {
      console.error('Error adding note:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditStart = (note) => {
    if (!canAddNotes) return;
    setEditingNoteId(note.id);
    setEditText(note.text);
    setDeleteConfirmId(null);
  };

  const handleEditCancel = () => {
    setEditingNoteId(null);
    setEditText('');
  };

  const handleEditSave = async (noteId) => {
    if (!editText.trim() || !canAddNotes) return;
    
    setEditSubmitting(true);
    try {
      await clientAPI.editNote(shareToken, noteId, editText.trim());
      setEditingNoteId(null);
      setEditText('');
      onNoteAdded();
    } catch (err) {
      console.error('Error editing note:', err);
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteConfirm = (noteId) => {
    if (!canAddNotes) return;
    setDeleteConfirmId(noteId);
    setEditingNoteId(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
  };

  const handleDelete = async (noteId) => {
    if (!canAddNotes) return;
    try {
      await clientAPI.deleteNote(shareToken, noteId);
      setDeleteConfirmId(null);
      onNoteAdded();
    } catch (err) {
      console.error('Error deleting note:', err);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div ref={sectionRef} style={{ 
      marginTop: '64px',
      paddingTop: '48px',
      borderTop: '1px solid var(--kahla-border)',
      maxWidth: '700px',
      margin: '64px auto 0'
    }}>
      <OwnerInlineText
        edit={!!ownerEdit}
        as="h2"
        value={txt.notes_title}
        onSave={(v) => ownerEdit?.save({ notes_section_title: v, use_workspace_defaults: false })}
        placeholder={t.notes_title}
        locked={ownerEdit?.locked}
        onLockedClick={ownerEdit?.onLockedClick}
        testId="cv-notes-title"
        style={{ 
        fontFamily: 'Playfair Display, serif',
        fontSize: '24px',
        color: 'var(--kahla-charcoal)',
        textAlign: 'center',
        marginBottom: '16px'
      }} />
      <OwnerInlineText
        edit={!!ownerEdit}
        as="p"
        multi
        value={txt.notes_subtitle}
        onSave={(v) => ownerEdit?.save({ notes_section_description: v, use_workspace_defaults: false })}
        placeholder={t.notes_subtitle}
        locked={ownerEdit?.locked}
        onLockedClick={ownerEdit?.onLockedClick}
        testId="cv-notes-subtitle"
        style={{ 
        textAlign: 'center',
        fontSize: '13px',
        color: 'var(--kahla-text-light)',
        marginBottom: '32px',
        fontStyle: 'italic'
      }} />

      {/* Mobile-only floating "Dodaj nową notatkę" pill — surfaces
          only when the user is reading inside the Notes section and
          the composer is still below the fold. Disappears smoothly
          as soon as the composer enters the viewport so it never
          competes with the actual input. Sits above the sticky
          bottom nav and respects iOS safe-area. */}
      {isNarrow && canAddNotes && (
        <button
          type="button"
          onClick={jumpToComposer}
          data-testid="notes-floating-add"
          aria-hidden={!showFloatingAddNote}
          style={{
            position: 'fixed',
            // Sit just above the bottom nav (which lives ~6px + safe-area)
            // so the two affordances don't visually collide.
            bottom: 'calc(env(safe-area-inset-bottom) + 56px)',
            left: '50%',
            transform: `translateX(-50%) translateY(${showFloatingAddNote ? '0' : '12px'})`,
            opacity: showFloatingAddNote ? 1 : 0,
            pointerEvents: showFloatingAddNote ? 'auto' : 'none',
            transition: 'transform 0.28s ease, opacity 0.24s ease',
            zIndex: 940, // below bottom nav (950), above page content
            padding: '8px 18px',
            fontFamily: '"Playfair Display", serif',
            fontSize: '11px',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--kahla-charcoal)',
            background: 'var(--kahla-cream)',
            border: '1px solid var(--kahla-border)',
            borderRadius: '999px',
            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {t.notes_jump_to_input} ↓
        </button>
      )}

      {/* Notes List */}
      <div style={{ marginBottom: '32px' }}>
        {notes.length === 0 ? (
          <p style={{ 
            textAlign: 'center', 
            color: 'var(--kahla-text-light)',
            padding: '40px',
            backgroundColor: 'var(--kahla-cream-light)',
            border: '1px solid var(--kahla-border)'
          }}>
            {t.no_notes}
          </p>
        ) : (
          <div
            ref={notesListRef}
            className="cv-chat-scroll"
            data-testid="notes-scroll-container"
            style={{
              display: 'flex', flexDirection: 'column', gap: '16px',
              maxHeight: '480px', overflowY: 'auto',
              padding: '4px 10px 4px 2px',
            }}
          >
            {notes.map((note) => (
              <div 
                key={note.id}
                data-testid={`client-note-${note.id}`}
                style={{
                  backgroundColor: note.author_type === 'owner' ? 'var(--kahla-cream-light)' : 'var(--kahla-white)',
                  border: '1px solid var(--kahla-border)',
                  padding: '20px 24px'
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <span style={{ 
                    fontSize: '11px',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: note.author_type === 'owner' ? 'var(--kahla-taupe)' : 'var(--kahla-text-light)',
                    fontWeight: '500'
                  }}>
                    {note.author_type === 'owner'
                      ? (t.from_studio || 'From {brand}').replace('{brand}', brandName || t.studio_name || 'Studio')
                      : (() => {
                          // Shared-decision: prefer participant name from note's participant_idx
                          if (typeof note.participant_idx === 'number' && participants[note.participant_idx]) {
                            return (t.from_named || 'From {name}').replace('{name}', participants[note.participant_idx]);
                          }
                          if (clientName && clientName.trim()) {
                            return (t.from_named || 'From {name}').replace('{name}', clientName.trim());
                          }
                          return (t.from_client_default || 'From Client');
                        })()}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ 
                      fontSize: '11px',
                      color: 'var(--kahla-text-light)'
                    }}>
                      {formatDate(note.created_at)}
                      {note.updated_at && note.updated_at !== note.created_at && (
                        <span style={{ fontStyle: 'italic' }}> {t.edited}</span>
                      )}
                    </span>
                    {/* Client can edit/delete their own notes when notes are allowed */}
                    {note.author_type === 'client' && canAddNotes && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          data-testid={`client-edit-note-${note.id}`}
                          onClick={() => handleEditStart(note)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '11px',
                            color: 'var(--kahla-taupe)',
                            padding: '2px 6px',
                            opacity: 0.7,
                            transition: 'opacity 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.opacity = 1}
                          onMouseLeave={(e) => e.target.style.opacity = 0.7}
                        >
                          {t.edit}
                        </button>
                        <button
                          data-testid={`client-delete-note-${note.id}`}
                          onClick={() => handleDeleteConfirm(note.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '11px',
                            color: '#c0392b',
                            padding: '2px 6px',
                            opacity: 0.7,
                            transition: 'opacity 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.opacity = 1}
                          onMouseLeave={(e) => e.target.style.opacity = 0.7}
                        >
                          {t.delete}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Delete Confirmation */}
                {deleteConfirmId === note.id && (
                  <div style={{
                    backgroundColor: '#fef3f2',
                    border: '1px solid #fecaca',
                    padding: '12px 16px',
                    marginBottom: '12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{ fontSize: '13px', color: '#991b1b' }}>
                      {t.delete_note_confirm}
                    </span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        data-testid={`client-confirm-delete-${note.id}`}
                        onClick={() => handleDelete(note.id)}
                        style={{
                          padding: '6px 12px',
                          fontSize: '12px',
                          backgroundColor: '#dc2626',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        {t.yes_delete}
                      </button>
                      <button
                        data-testid={`client-cancel-delete-${note.id}`}
                        onClick={handleDeleteCancel}
                        style={{
                          padding: '6px 12px',
                          fontSize: '12px',
                          backgroundColor: 'white',
                          color: 'var(--kahla-text)',
                          border: '1px solid var(--kahla-border)',
                          cursor: 'pointer'
                        }}
                      >
                        {t.cancel}
                      </button>
                    </div>
                  </div>
                )}

                {/* Edit Mode */}
                {editingNoteId === note.id ? (
                  <div>
                    <textarea
                      data-testid={`client-edit-textarea-${note.id}`}
                      value={editText}
                      onChange={(e) => setEditText(e.target.value.slice(0, 500))}
                      style={{
                        width: '100%',
                        minHeight: '80px',
                        padding: '12px',
                        border: '1px solid var(--kahla-border)',
                        backgroundColor: 'var(--kahla-cream-light)',
                        fontSize: '14px',
                        resize: 'none',
                        fontFamily: 'Inter, sans-serif'
                      }}
                    />
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: '10px'
                    }}>
                      <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)' }}>
                        {editText.length}/500
                      </span>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          data-testid={`client-cancel-edit-${note.id}`}
                          onClick={handleEditCancel}
                          className="btn-secondary"
                          style={{ padding: '8px 16px', fontSize: '12px' }}
                        >
                          {t.cancel}
                        </button>
                        <button
                          data-testid={`client-save-edit-${note.id}`}
                          onClick={() => handleEditSave(note.id)}
                          disabled={!editText.trim() || editSubmitting}
                          className="btn-primary"
                          style={{ padding: '8px 16px', fontSize: '12px' }}
                        >
                          {editSubmitting ? t.saving : t.save}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {note.text && (
                      <p style={{ 
                        fontSize: '14px',
                        lineHeight: '1.7',
                        color: 'var(--kahla-text)',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {note.text}
                      </p>
                    )}
                    {note.attachment && (
                      <NoteAttachmentCard attachment={note.attachment} documents={documents} images={images} t={t} />
                    )}
                    {note.id === lastClientNote?.id && lastClientNote?.seen_by_owner_at && (
                      <p data-testid="note-seen-indicator" style={{
                        fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase',
                        fontStyle: 'italic', color: 'var(--kahla-text-light)',
                        textAlign: 'right', margin: '6px 0 0', opacity: 0.8,
                      }}>
                        {t.seen_label || 'Seen'}
                      </p>
                    )}
                  </>
                )}
              </div>
            ))}
            <div ref={notesEndRef} aria-hidden="true" />
          </div>
        )}
      </div>

      {/* Add Note Form */}
      {canAddNotes && (
        <div
          ref={composerRef}
          data-testid="notes-composer"
          style={{
          backgroundColor: 'var(--kahla-white)',
          border: '1px solid var(--kahla-border)',
          padding: '20px 24px'
        }}>
          <textarea
            ref={textareaRef}
            value={newNote}
            onChange={(e) => setNewNote(e.target.value.slice(0, 500))}
            placeholder={t.note_placeholder}
            style={{
              width: '100%',
              minHeight: '100px',
              padding: '12px',
              border: '1px solid var(--kahla-border)',
              backgroundColor: 'var(--kahla-cream-light)',
              fontSize: '14px',
              resize: 'none',
              fontFamily: 'Inter, sans-serif'
            }}
          />
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '12px'
          }}>
            <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)' }}>
              {newNote.length}/500
            </span>
            <button
              className="btn-secondary"
              onClick={handleSubmit}
              disabled={!newNote.trim() || submitting}
              style={{ padding: '10px 24px' }}
            >
              {submitting ? t.adding : t.add_note_btn}
            </button>
          </div>
        </div>
      )}

      {!canAddNotes && (
        <p style={{ 
          textAlign: 'center',
          fontSize: '12px',
          color: 'var(--kahla-text-light)',
          fontStyle: 'italic',
          marginTop: '16px'
        }}>
          {t.notes_closed}
        </p>
      )}
    </div>
  );
};

// Image Card Component with reactions
const ImageCard = ({ image, isLocked, onReaction, onComment, onImageClick, finalDecisionMode, onToggleFinal, t, sharedMode, participantId, participants, brandName = '', allowImageConversations = true, reactionMode = 'mood' }) => {
  // In shared mode, get my reaction from the per-participant map
  const myReaction = sharedMode && participantId != null
    ? (image.reactions?.[String(participantId)]?.reaction || null)
    : image.reaction;
  const myComment = sharedMode && participantId != null
    ? (image.reactions?.[String(participantId)]?.comment || '')
    : (image.comment || '');
  const otherPid = participantId === 0 ? 1 : 0;
  const otherReaction = sharedMode ? (image.reactions?.[String(otherPid)]?.reaction || null) : null;

  const [comment, setComment] = useState(myComment);
  const [showComment, setShowComment] = useState(!!myComment);

  // Sync comment when participant switches
  React.useEffect(() => { setComment(myComment); setShowComment(!!myComment); }, [myComment]);

  const handleCommentBlur = () => {
    if (comment !== image.comment) {
      onComment(comment);
    }
  };

  return (
    <div 
      className="image-card" 
      style={{ 
        touchAction: 'auto',
        boxShadow: image.is_final ? '0 0 0 2px #C9A96E' : 'none',
        transition: 'box-shadow 0.3s ease',
      }}
    >
      <div 
        className="image-card-image"
        data-testid={`client-image-clickable-${image.id}`}
        onClick={onImageClick}
        style={{ cursor: 'zoom-in', touchAction: 'auto', position: 'relative' }}
      >
        <img src={image.image_data} alt="" style={{ pointerEvents: 'none', touchAction: 'auto' }} />
        {image.is_final && (
          <span
            data-testid={`client-final-badge-${image.id}`}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              backgroundColor: 'rgba(201,169,110,0.92)',
              color: '#fff',
              fontSize: '9px',
              fontWeight: '600',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              padding: '3px 8px',
              pointerEvents: 'none',
            }}
          >
            Final
          </span>
        )}
      </div>
      
      <div className="image-card-footer">
        {/* Section label — minimal text caption, sits above reactions */}
        {image.section_label && (
          <div
            className="image-section-label"
            data-testid={`client-image-label-${image.id}`}
          >
            {image.section_label}
          </div>
        )}

        {/* Reactions — labels swap based on the template's reaction mode.
            Internal values (love/neutral/not_me) stay constant so we don't
            need a data migration when the mode changes.

            On Inspiration Boards the caller passes `onReaction={null}` and
            we drop the entire bar (no greyed-out controls, no empty
            placeholder — the surface stays intentionally minimal). */}
        {onReaction && (
          <>
            <div className="image-reactions" data-reaction-mode={reactionMode}>
              {getReactionButtons(reactionMode, t).map(({ value, label, icon }) => {
                const slug = value === 'not_me' ? 'not-me' : value;
                const isActive = myReaction === value;
                const showAsText = !icon; // simple / decision modes
                return (
                  <button
                    key={value}
                    data-testid={`reaction-btn-${value}-${image.id}`}
                    className={`reaction-btn ${slug} ${isActive ? 'active' : ''} ${showAsText ? 'is-text' : ''}`}
                    onClick={() => !isLocked && onReaction(value)}
                    disabled={isLocked}
                    title={label}
                    style={showAsText ? {
                      padding: '6px 14px',
                      fontFamily: 'inherit',
                      fontSize: '11px',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: isActive ? 'var(--kahla-charcoal)' : 'var(--kahla-text-light)',
                      background: isActive ? 'var(--kahla-cream-light)' : 'transparent',
                      border: `1px solid ${isActive ? 'var(--kahla-charcoal)' : 'var(--kahla-border)'}`,
                      borderRadius: 0,
                      cursor: isLocked ? 'not-allowed' : 'pointer',
                      transition: 'background 0.15s, color 0.15s, border-color 0.15s',
                    } : undefined}
                  >
                    {icon || label}
                  </button>
                );
              })}
            </div>

            <div style={{ textAlign: 'center', marginTop: '8px' }}>
              <span style={{ fontSize: '12px', color: 'var(--kahla-text-light)' }}>
                {myReaction ? getReactionLabel(reactionMode, myReaction, t).label : ''}
              </span>
            </div>
          </>
        )}

        {/* Other person's reaction (shared mode) */}
        {sharedMode && otherReaction && (
          <div data-testid={`other-reaction-${image.id}`} style={{
            textAlign: 'center', marginTop: '6px', padding: '4px 8px',
            background: 'rgba(0,0,0,0.02)', borderTop: '1px solid var(--kahla-border)',
          }}>
            <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)', opacity: 0.7 }}>
              {participants?.[otherPid] || `Person ${otherPid + 1}`}:{' '}
              {(() => {
                const lbl = getReactionLabel(reactionMode, otherReaction, t);
                return lbl.icon ? lbl.icon : lbl.shortLabel;
              })()}
            </span>
          </div>
        )}

        {/* Final Choice toggle — only visible when final_decision_mode is ON */}
        {finalDecisionMode && (
          <div style={{ textAlign: 'center', marginTop: '12px' }}>
            <button
              onClick={() => onToggleFinal(image.id)}
              data-testid={`client-final-toggle-${image.id}`}
              style={{
                background: 'none',
                border: image.is_final ? '1px solid #C9A96E' : '1px solid var(--kahla-border)',
                cursor: 'pointer',
                fontSize: '12px',
                padding: '6px 16px',
                color: image.is_final ? '#C9A96E' : 'var(--kahla-text-light)',
                letterSpacing: '0.06em',
                transition: 'all 0.2s ease',
                backgroundColor: image.is_final ? 'rgba(201,169,110,0.08)' : 'transparent',
              }}
            >
              {image.is_final ? t.final_choice : t.mark_final}
            </button>
          </div>
        )}

        {/* Comment toggle — gated by template plan. When Image Conversations
            are disabled, the per-image note affordance is hidden entirely. */}
        {allowImageConversations && image.reaction && !isLocked && (
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            {!showComment ? (
              <button 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: 'var(--kahla-text-light)',
                  fontSize: '12px',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                onClick={() => setShowComment(true)}
              >
                {t.add_note}
              </button>
            ) : (
              <div className="image-comment">
                <textarea 
                  value={comment}
                  onChange={(e) => setComment(e.target.value.slice(0, 200))}
                  onBlur={handleCommentBlur}
                  placeholder={t.comment_placeholder}
                  maxLength={200}
                  disabled={isLocked}
                  style={{ fontSize: '13px' }}
                />
                <p style={{ fontSize: '10px', color: 'var(--kahla-text-light)', textAlign: 'right', marginTop: '4px', fontStyle: 'italic' }}>
                  One or two sentences are enough.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Show existing comment when locked */}
        {allowImageConversations && isLocked && image.comment && (
          <div style={{ marginTop: '12px', padding: '12px', backgroundColor: 'var(--kahla-cream-light)' }}>
            <p style={{ fontSize: '13px', fontStyle: 'italic', color: 'var(--kahla-text)', whiteSpace: 'pre-wrap' }}>
              "{image.comment}"
            </p>
          </div>
        )}

        {/* Owner reply — visible on this specific image. Distinct from
            client's own comment via the side-accent and label, so the
            two voices read as a calm visual conversation. */}
        {allowImageConversations && image.owner_reply && (
          <div
            data-testid={`image-owner-reply-${image.id}`}
            style={{
              marginTop: image.comment || (isLocked && image.comment) ? '10px' : '16px',
              padding: '10px 14px',
              backgroundColor: 'var(--kahla-cream-light)',
              borderLeft: '2px solid var(--kahla-taupe)',
            }}
          >
            <span style={{
              fontSize: '10px',
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: 'var(--kahla-taupe)',
              fontWeight: 500,
            }}>
              {brandName ? `From ${brandName}` : (t.from_studio || 'From {brand}').replace('{brand}', t.studio_name || 'Studio')}
            </span>
            <p style={{
              fontSize: '13px',
              color: 'var(--kahla-text)',
              marginTop: '4px',
              fontStyle: 'italic',
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
            }}>
              {image.owner_reply}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Client Documents Section (read-only)
const ClientDocumentsSection = ({ documents, shareToken, t, txt, ownerEdit = null }) => {
  const [confirmations, setConfirmations] = React.useState({});
  const [confirming, setConfirming] = React.useState(null);

  // Initialize from server data
  React.useEffect(() => {
    const initial = {};
    documents.forEach(doc => {
      if (doc.confirmed_at) initial[doc.id] = doc.confirmed_at;
    });
    setConfirmations(initial);
  }, [documents]);

  const getIcon = (doc) => {
    if (doc.type === 'link') return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
      </svg>
    );
    if (doc.file_type === 'pdf') return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
      </svg>
    );
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
      </svg>
    );
  };

  const openDoc = async (doc) => {
    try {
      await documentsAPI.markViewed(shareToken, doc.id);
    } catch (err) {
      // Non-blocking
    }
    if (doc.type === 'link') {
      window.open(doc.url, '_blank', 'noopener,noreferrer');
    } else {
      // Open through the branded client-document route on the SAME
      // origin the client is currently browsing (e.g. maestra.space),
      // not the preview emergent.host alias in REACT_APP_BACKEND_URL.
      // The route also hides the underlying storage_key entirely.
      const brandedUrl = `${window.location.origin}/api/client/board/${shareToken}/document/${doc.id}`;
      window.open(brandedUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleConfirm = async (docId) => {
    setConfirming(docId);
    try {
      const { data } = await documentsAPI.confirmRead(shareToken, docId);
      setConfirmations(prev => ({ ...prev, [docId]: data.confirmed_at }));
    } catch (err) {
      // Silently fail
    } finally {
      setConfirming(null);
    }
  };

  const formatConfirmDate = (isoStr) => {
    const d = new Date(isoStr);
    return `${d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })}, ${d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}`;
  };

  return (
    <div style={{
      marginTop: '48px',
      paddingTop: '32px',
      borderTop: '1px solid var(--kahla-border)'
    }}>
      <OwnerInlineText
        edit={!!ownerEdit}
        as="h2"
        value={(txt || t).documents_title}
        onSave={(v) => ownerEdit?.save({ documents_section_title: v, use_workspace_defaults: false })}
        placeholder={t.documents_title}
        locked={ownerEdit?.locked}
        onLockedClick={ownerEdit?.onLockedClick}
        testId="cv-documents-title"
        style={{
        fontFamily: 'Playfair Display, serif',
        fontSize: '22px',
        color: 'var(--kahla-charcoal)',
        marginBottom: '8px'
      }} />
      <OwnerInlineText
        edit={!!ownerEdit}
        as="p"
        multi
        value={(txt || t).documents_subtitle}
        onSave={(v) => ownerEdit?.save({ documents_section_description: v, use_workspace_defaults: false })}
        placeholder={t.documents_subtitle}
        locked={ownerEdit?.locked}
        onLockedClick={ownerEdit?.onLockedClick}
        testId="cv-documents-subtitle"
        style={{
        fontSize: '13px',
        color: 'var(--kahla-text-light)',
        marginBottom: '20px'
      }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
        {documents.map((doc) => {
          const isConfirmed = !!confirmations[doc.id];
          const isThisConfirming = confirming === doc.id;

          return (
            <div key={doc.id} style={{ border: '1px solid var(--kahla-border)', backgroundColor: 'var(--kahla-white)' }}>
              {/* Clickable row */}
              <div
                data-testid={`client-doc-${doc.id}`}
                onClick={() => openDoc(doc)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '14px 16px',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--kahla-cream-light)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--kahla-white)'}
              >
                <div style={{ color: 'var(--kahla-taupe)', flexShrink: 0 }}>
                  {getIcon(doc)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '14px',
                    color: 'var(--kahla-charcoal)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {doc.title || doc.original_filename || doc.url}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--kahla-text-light)', marginTop: '2px' }}>
                    {doc.type === 'file' ? (
                      <>{doc.file_type.toUpperCase()} · {doc.file_size_display}</>
                    ) : (
                      t.external_link
                    )}
                  </div>
                </div>
                <div style={{ color: 'var(--kahla-taupe)', fontSize: '11px', flexShrink: 0 }}>
                  {doc.type === 'file' ? t.open : t.visit}
                  <span style={{ marginLeft: '4px' }}>→</span>
                </div>
              </div>

              {/* Confirm action */}
              <div style={{
                padding: '0 16px 12px',
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
                {isConfirmed ? (
                  <span
                    data-testid={`client-doc-confirmed-${doc.id}`}
                    style={{
                      fontSize: '11px',
                      color: '#15803d',
                      fontWeight: 500,
                      letterSpacing: '0.01em'
                    }}
                  >
                    {'\u2713'} {t.confirmed_on} {formatConfirmDate(confirmations[doc.id])}
                  </span>
                ) : (
                  <button
                    data-testid={`client-doc-confirm-btn-${doc.id}`}
                    onClick={(e) => { e.stopPropagation(); handleConfirm(doc.id); }}
                    disabled={isThisConfirming}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: isThisConfirming ? 'default' : 'pointer',
                      fontSize: '11px',
                      color: 'var(--kahla-taupe)',
                      padding: '4px 0',
                      letterSpacing: '0.02em',
                      opacity: isThisConfirming ? 0.5 : 0.7,
                      transition: 'opacity 0.2s, color 0.2s',
                      fontFamily: 'Inter, sans-serif'
                    }}
                    onMouseEnter={(e) => { if (!isThisConfirming) { e.target.style.opacity = 1; e.target.style.color = 'var(--kahla-charcoal)'; } }}
                    onMouseLeave={(e) => { e.target.style.opacity = 0.7; e.target.style.color = 'var(--kahla-taupe)'; }}
                  >
                    {isThisConfirming ? t.confirming : t.i_have_read}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ClientView;
